import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import { useTranslation } from "react-i18next";
import CssBaseline from "@mui/material/CssBaseline";
import Sidenav from "examples/Sidenav";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import { directionDe } from "i18n";
import routes from "routes";
import { useMaterialUIController, setMiniSidenav, setDirection } from "context";
import { AuthProvider, useAuth } from "./context/AuthContext";
import brandWhite from "assets/images/logo-ct.png";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import LanguageSelector from "components/LanguageSelector";
import Icon from "@mui/material/Icon";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Caches emotion : en RTL, stylis-plugin-rtl inverse left/right, marges et paddings des styles MUI
const cacheLtr = createCache({ key: "muiltr" });
const cacheRtl = createCache({ key: "muirtl", stylisPlugins: [rtlPlugin] });

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/authentication/sign-in" replace />;
  return children;
}

function MobileNav({ filteredRoutes }) {
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleNavigate = (route) => {
    navigate(route);
    setDrawerOpen(false);
  };

  return (
    <>
      {/* Barre de navigation mobile en haut */}
      <MDBox sx={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1300,
        background: "linear-gradient(195deg, #42424a, #191919)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: 2, py: 1.5, boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
      }}>
        {/* Logo + nom */}
        <MDBox display="flex" alignItems="center" gap={1}>
          <img src={brandWhite} alt="logo" style={{ height: 28 }} />
          <MDTypography variant="button" sx={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
            {t("app.titre")}
          </MDTypography>
        </MDBox>

        {/* Bouton hamburger */}
        <MDBox
          onClick={() => setDrawerOpen((prev) => !prev)}
          sx={{
            width: 36, height: 36, borderRadius: "8px",
            background: drawerOpen ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.1)",
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.2s",
            userSelect: "none",
          }}>
          <Icon sx={{ color: "#fff", fontSize: 22, pointerEvents: "none" }}>
            {drawerOpen ? "close" : "menu"}
          </Icon>
        </MDBox>
      </MDBox>

      {/* Drawer menu mobile — à gauche */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: "linear-gradient(195deg, #42424a, #191919)",
          }
        }}>
        <MDBox sx={{ p: 2 }}>

          {/* Header utilisateur */}
          <MDBox display="flex" alignItems="center" gap={1.5} mb={3} mt={1}>
            <MDBox sx={{
              width: 42, height: 42, borderRadius: "50%",
              background: "linear-gradient(135deg, #1565c0, #6a1b9a)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <MDTypography variant="caption" sx={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
                {(user?.prenom?.[0] || "")}{(user?.nom?.[0] || "")}
              </MDTypography>
            </MDBox>
            <MDBox flex={1}>
              <MDTypography variant="button" sx={{ color: "#fff", fontWeight: 700, display: "block" }}>
                {user?.prenom} {user?.nom}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                {user?.role === "admin" ? t("compte.roleAdmin") : t("compte.roleEnseignant")}
              </MDTypography>
            </MDBox>
            <MDBox
              onClick={() => setDrawerOpen(false)}
              sx={{
                width: 30, height: 30, borderRadius: "50%",
                background: "rgba(255,255,255,0.1)", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
              <Icon sx={{ color: "#fff", fontSize: 16, pointerEvents: "none" }}>close</Icon>
            </MDBox>
          </MDBox>

          {/* Menu items */}
          {filteredRoutes.filter((r) => r.type === "collapse").map((route) => (
            <MDBox key={route.key}
              onClick={() => handleNavigate(route.route)}
              display="flex" alignItems="center" gap={2}
              sx={{
                py: 1.5, px: 2, mb: 0.5, borderRadius: "10px", cursor: "pointer",
                color: "rgba(255,255,255,0.7)", transition: "all 0.2s",
                "&:hover": { background: "rgba(255,255,255,0.1)", color: "#fff" },
                "&:active": { background: "rgba(255,255,255,0.15)" },
              }}>
              <MDBox sx={{ display: "flex", alignItems: "center", color: "inherit", pointerEvents: "none" }}>
                {route.icon}
              </MDBox>
              <MDTypography variant="button" sx={{ color: "inherit", fontSize: 14, pointerEvents: "none" }}>
                {route.name}
              </MDTypography>
            </MDBox>
          ))}

          {/* Divider */}
          <MDBox sx={{ borderTop: "1px solid rgba(255,255,255,0.1)", my: 2 }} />

          {/* Langue */}
          <LanguageSelector variant="drawer" />

          {/* Mon profil */}
          <MDBox
            onClick={() => handleNavigate("/profil")}
            display="flex" alignItems="center" gap={2}
            sx={{
              py: 1.5, px: 2, mb: 0.5, borderRadius: "10px", cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { background: "rgba(255,255,255,0.1)" },
              "&:active": { background: "rgba(255,255,255,0.15)" },
            }}>
            <Icon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 20, pointerEvents: "none" }}>manage_accounts</Icon>
            <MDTypography variant="button" sx={{ color: "rgba(255,255,255,0.7)", fontSize: 14, pointerEvents: "none" }}>
              {t("compte.profil")}
            </MDTypography>
          </MDBox>

          {/* Déconnexion */}
          <MDBox
            onClick={logout}
            display="flex" alignItems="center" gap={2}
            sx={{
              py: 1.5, px: 2, borderRadius: "10px", cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { background: "rgba(255,255,255,0.1)" },
              "&:active": { background: "rgba(255,255,255,0.15)" },
            }}>
            <Icon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 20, pointerEvents: "none" }}>logout</Icon>
            <MDTypography variant="button" sx={{ color: "rgba(255,255,255,0.7)", fontSize: 14, pointerEvents: "none" }}>
              {t("compte.deconnexion")}
            </MDTypography>
          </MDBox>

        </MDBox>
      </Drawer>
    </>
  );
}

function LogoutButton() {
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  return (
    <MDBox sx={{
      position: "fixed", bottom: 0, left: 0, width: "250px",
      background: "linear-gradient(195deg, #42424a, #191919)",
      borderTop: "1px solid rgba(255,255,255,0.1)",
      padding: "12px 16px", zIndex: 1300,
    }}>
      <MDBox display="flex" alignItems="center" gap={1.5} mb={1.5}>
        <MDBox sx={{
          width: 36, height: 36, borderRadius: "50%",
          background: "linear-gradient(135deg, #1565c0, #6a1b9a)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <MDTypography variant="caption" sx={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
            {(user?.prenom?.[0] || "")}{(user?.nom?.[0] || "")}
          </MDTypography>
        </MDBox>
        <MDBox sx={{ overflow: "hidden" }}>
          <MDTypography variant="button" sx={{
            color: "#fff", fontWeight: 700, display: "block",
            fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {user?.prenom} {user?.nom}
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>
            {user?.role === "admin" ? t("compte.roleAdmin") : t("compte.roleEnseignant")}
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox mb={1}>
        <LanguageSelector variant="drawer" dense />
      </MDBox>

      {/* Profil et Déconnexion côte à côte (gain ~44px de hauteur) : le menu de la Sidenav
          doit tenir au-dessus de ce bloc fixe sur une zone visible de 660px de haut */}
      <MDBox display="flex" gap={0.75}>
        {[
          { onClick: () => navigate("/profil"), icon: "manage_accounts", label: t("compte.profil") },
          { onClick: logout, icon: "logout", label: t("compte.deconnexion") },
        ].map((b) => (
          <MDBox key={b.icon} onClick={b.onClick} title={b.label}
            display="flex" alignItems="center" justifyContent="center" gap={0.5}
            sx={{
              flex: "1 1 auto", minWidth: 0,
              cursor: "pointer", padding: "8px 8px", borderRadius: "8px",
              background: "rgba(255,255,255,0.08)", transition: "all 0.2s",
              "&:hover": { background: "rgba(255,255,255,0.15)" },
            }}>
            <Icon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 18, flexShrink: 0 }}>{b.icon}</Icon>
            <MDTypography variant="button" sx={{
              color: "rgba(255,255,255,0.7)", fontSize: 12,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {b.label}
            </MDTypography>
          </MDBox>
        ))}
      </MDBox>
    </MDBox>
  );
}

function AppContent() {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, sidenavColor, layout } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { user } = useAuth();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const { t, i18n } = useTranslation();
  const direction = directionDe(i18n.resolvedLanguage);

  useEffect(() => {
    document.documentElement.setAttribute("dir", direction);
    document.documentElement.setAttribute("lang", i18n.resolvedLanguage);
    setDirection(dispatch, direction);
  }, [direction, i18n.resolvedLanguage, dispatch]);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const filteredRoutes = routes.filter((route) => {
    if (!user) return route.key === "sign-in";
    if (user.role === "enseignant") {
      if (route.type === "collapse" && ["enseignants", "admins", "tableau-general", "traductions"].includes(route.key)) {
        return false;
      }
      return true;
    }
    return true;
  });

  const allRoutes = user ? filteredRoutes : routes;

  // Libellés de menu traduits (le "name" de routes.js sert de valeur par défaut)
  const menuRoutes = filteredRoutes.map((route) =>
    route.name ? { ...route, name: t(`menu.${route.key}`, { defaultValue: route.name }) } : route
  );

  const getRoutes = (allR) =>
    allR.map((route) => {
      if (route.collapse) return getRoutes(route.collapse);
      if (route.route) {
        if (route.key === "sign-in") {
          return (
            <Route exact path={route.route}
              element={route.component}
              key={route.key}
            />
          );
        }
        return (
          <Route exact path={route.route}
            element={<PrivateRoute>{route.component}</PrivateRoute>}
            key={route.key}
          />
        );
      }
      return null;
    });

  return (
    <CacheProvider value={direction === "rtl" ? cacheRtl : cacheLtr}>
    <ThemeProvider theme={direction === "rtl" ? themeRTL : theme}>
      <CssBaseline />
      {layout === "dashboard" && user && (
        <>
          {isMobile ? (
            <MobileNav filteredRoutes={menuRoutes} />
          ) : (
            <>
              <Sidenav
                color={sidenavColor}
                brandName={t("app.titre")}
                routes={menuRoutes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              <LogoutButton />
            </>
          )}
        </>
      )}

      <MDBox sx={{ pt: isMobile && layout === "dashboard" && user ? "60px" : 0 }}>
        <Routes>
          {getRoutes(allRoutes)}
          <Route
            path="/"
            element={
              user
                ? <Navigate to="/dashboard" replace />
                : <Navigate to="/authentication/sign-in" replace />
            }
          />
          <Route
            path="*"
            element={
              user
                ? <Navigate to="/dashboard" replace />
                : <Navigate to="/authentication/sign-in" replace />
            }
          />
        </Routes>
      </MDBox>
    </ThemeProvider>
    </CacheProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}